import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * WordCards — a grid of face-down word cards for small "meet these words"
 * sets (lesson 3 uses it for the no-ة feminines). Tapping a card plays the
 * word and reveals its meaning, an example sentence, and the shared badge
 * (plus an optional per-card note). Completes when every card is open.
 *
 * The headword's audio can be decoupled from its printed form with an
 * optional `audio_ar`: lesson 27's shape cards print an abstract pattern
 * (فُعُول) that the TTS mispronounces because it is not a real word, so they
 * voice a real word of the same rhythm instead while the card still shows
 * the pattern itself.
 */
const headwordAudio = (card) => card.audio_ar || card.arabic;

export default function WordCards({ data, onComplete, audio }) {
  const [openedIds, setOpenedIds] = useState(() => new Set());

  const cards = data.cards;
  const allOpened = openedIds.size >= cards.length;

  useEffect(() => {
    if (allOpened) onComplete();
  }, [allOpened, onComplete]);

  const handleOpen = (card) => {
    if (openedIds.has(card.id)) return;
    setOpenedIds((prev) => new Set(prev).add(card.id));
    audio.play(headwordAudio(card));
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{openedIds.size}/{cards.length} opened</ProgressPill>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((card) => {
          const opened = openedIds.has(card.id);
          const word = (
            <span dir="rtl" lang="ar" className="font-arabic text-4xl leading-[1.8] block" style={{ color: "var(--parchment)" }}>
              {card.arabic}
            </span>
          );
          // An opened card must not be a (disabled) button — that would swallow
          // clicks on the nested PlayButtons.
          if (!opened) {
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => handleOpen(card)}
                className="rounded-xl px-4 pt-2 pb-3 text-center transition-all hover:brightness-110"
                style={{ background: "var(--ink)", border: "1px dashed var(--panel-line)" }}
              >
                {word}
                <span className="text-sm block mt-1" style={{ color: "var(--parchment-dim)" }}>
                  Tap to reveal
                </span>
              </button>
            );
          }
          return (
            <div
              key={card.id}
              className="rounded-xl px-4 pt-2 pb-3 text-center"
              style={{ background: "var(--ink)", border: "1px solid var(--gold-dim)" }}
            >
              <span className="flex items-center justify-center gap-2.5">
                {word}
                {audio.audioAllowed && (
                  <PlayButton text={headwordAudio(card)} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </span>
              {data.badge && (
                <span
                  dir="rtl"
                  className="inline-flex items-center rounded-2xl px-2.5 py-0.5 mt-1 text-[0.72rem] font-semibold"
                  style={{ background: "var(--violet-bg)", color: "var(--tag-violet)" }}
                >
                  {data.badge}
                </span>
              )}
              <span className="text-sm block mt-1.5" style={{ color: "var(--parchment)" }}>{card.en}</span>
              <span className="flex items-center justify-center gap-2 mt-2">
                <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.7]" style={{ color: "var(--gold-bright)" }}>
                  {card.example_ar}
                </span>
                {audio.audioAllowed && (
                  <PlayButton text={card.example_ar} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </span>
              <span className="text-xs block mt-0.5" style={{ color: "var(--parchment-dim)" }}>{card.example_en}</span>
              {card.note && (
                <span className="text-xs block mt-1.5 italic" style={{ color: "var(--parchment-dim)" }}>
                  <MixedText text={card.note} />
                </span>
              )}
            </div>
          );
        })}
      </div>

      {allOpened && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">👀</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
